import React, { useState, useRef, useEffect } from "react";
import { Box } from "@mui/material";
import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import { Benefit } from "../../../new-reports/types/newReportTypes";

type Props = {
  benefits: Benefit[] | undefined;
  label: string;
  checkIconSrc: string;
  backgroundColor?: string; // Optional background color (for PNOE benefits)
  gridColumns?: number; // Number of columns for grid layout (1 = single column, 2+ = grid)
  clickable?: boolean; // Whether benefits are clickable
  onBenefitClick?: (benefitName: string) => void; // Callback when benefit is clicked
  // Optional font size overrides
  fontSizes?: {
    sectionTitle?: string;
    benefitText?: string;
  };
};

const ServiceBenefitsSection = ({
  benefits,
  label,
  checkIconSrc,
  backgroundColor,
  gridColumns = 1,
  clickable = false,
  onBenefitClick,
  fontSizes,
}: Props) => {
  const theme: Theme = useTheme();
  const [selectedBenefit, setSelectedBenefit] = useState<Benefit | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Default font sizes (from ServicePage)
  const defaultFontSizes = {
    sectionTitle: "11px",
    benefitText: "9px",
  };

  // Merge with provided overrides
  const sizes = { ...defaultFontSizes, ...fontSizes };

  // Handle click outside to close popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        // Check if click is on a benefit row
        const target = event.target as HTMLElement;
        const clickedBenefit = target.closest("[data-benefit-row]");
        if (!clickedBenefit) {
          setSelectedBenefit(null);
        }
      }
    };

    if (selectedBenefit) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedBenefit]);

  if (!benefits || benefits.length === 0) {
    return null;
  }

  const handleBenefitClick = (benefit: Benefit) => {
    if (clickable) {
      if (onBenefitClick) {
        onBenefitClick(benefit.name);
      }

      // Toggle: if clicking the same benefit, close it; otherwise, show new one
      if (selectedBenefit?.name === benefit.name) {
        setSelectedBenefit(null);
      } else {
        setSelectedBenefit(benefit);
      }
    }
  };

  return (
    <Box sx={styles.section}>
      <Box component="p" sx={styles.sectionTitle(theme, sizes.sectionTitle)}>
        {label}
      </Box>
      <Box
        sx={
          gridColumns > 1
            ? styles.benefitsBoxGrid(theme, gridColumns, backgroundColor)
            : styles.benefitsBoxInline(theme, backgroundColor)
        }
      >
        {benefits.map((benefit, idx) => (
          <Box
            key={idx}
            data-benefit-row
            sx={styles.benefitRow(
              theme,
              clickable,
              label === "Additional benefits",
              benefit.text !== ""
            )}
            onClick={() => handleBenefitClick(benefit)}
          >
            <Box
              component="img"
              src={checkIconSrc}
              alt="check"
              sx={styles.checkIcon}
            />
            <Box
              component="p"
              sx={styles.benefitText(theme, sizes.benefitText)}
            >
              {benefit.name}
            </Box>
            {/* Popover for benefit description */}
            {selectedBenefit &&
              selectedBenefit.text !== "" &&
              selectedBenefit.name === benefit.name && (
                <Box ref={popoverRef} sx={styles.popover(theme)}>
                  <Box component="p" sx={styles.popoverTitle(theme)}>
                    {selectedBenefit.name}
                  </Box>
                  <Box component="p" sx={styles.popoverDescription(theme)}>
                    {selectedBenefit.text}
                  </Box>
                </Box>
              )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ServiceBenefitsSection;

const styles = {
  section: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  sectionTitle: (theme: Theme, fontSize: string) => ({
    fontFamily: "var(--font-Regular)",
    fontSize,
    lineHeight: "normal",
    color: theme.colorTokens.N[90],
    margin: 0,
  }),
  // Inline (single column) layout
  benefitsBoxInline: (theme: Theme, backgroundColor?: string) => ({
    backgroundColor: backgroundColor || "transparent",
    border: backgroundColor
      ? `0.5px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`
      : `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "12px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  }),
  // Grid layout (2+ columns)
  benefitsBoxGrid: (
    theme: Theme,
    columns: number,
    backgroundColor?: string
  ) => ({
    backgroundColor: backgroundColor || "transparent",
    border: backgroundColor
      ? `0.5px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`
      : `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "12px",
    display: "grid",
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap: "12px 16px", // row gap, column gap
  }),
  benefitRow: (
    theme: Theme,
    clickable: boolean,
    isAdditionalBenefit: boolean,
    hasText: boolean
  ) => ({
    display: "flex",
    gap: "6px",
    alignItems: "center",
    padding: clickable ? "8px" : "0",
    borderRadius: "8px",
    position: "relative",
    border:
      clickable && hasText
        ? `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`
        : "none",
    backgroundColor: !clickable
      ? "transparent"
      : isAdditionalBenefit && hasText
      ? theme.colorTokens.N[20]
      : "#fff",
    cursor: clickable && hasText ? "pointer" : "default",
    "&:hover":
      clickable && hasText
        ? {
            backgroundColor: theme.colorTokens.N[30],
          }
        : {},
  }),
  checkIcon: {
    width: "14px",
    height: "14px",
    flexShrink: 0,
  },
  benefitText: (theme: Theme, fontSize: string) => ({
    fontFamily: "var(--font-Regular)",
    fontSize,
    lineHeight: "14px",
    color: theme.colorTokens.N[90],
    margin: 0,
  }),
  // Popover styles (based on Figma design)
  popover: (theme: Theme) => ({
    position: "absolute" as "absolute",
    top: "calc(100% + 8px)", // 8px below the benefitRow
    left: "0px",
    width: "100%", // Dynamic width based on benefitRow
    backgroundColor: "#E6F0FF", // Light blue from Figma
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`, // #E1E3E5
    borderRadius: "8px",
    padding: "8px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
    zIndex: 1000,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  }),
  popoverTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colorTokens.N[90], // #241F1F equivalent (using N[90] as closest)
    margin: 0,
  }),
  popoverDescription: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "15px",
    color: theme.colorTokens.N[90], // #3B3F42 (N[90])
    margin: 0,
    whiteSpace: "pre-wrap" as "pre-wrap",
  }),
};
